import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';

export class CapacitorUtils {
  static isNative() {
    return Capacitor.isNativePlatform();
  }

  static getPlatform() {
    return Capacitor.getPlatform();
  }

  static async initializeApp() {
    if (this.isNative()) {
      // Hide splash screen
      await SplashScreen.hide();
      
      // Set status bar style
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#121212' });
      
      // Request push notification permissions
      await this.requestPushPermissions();
    }
  }

  static async requestPushPermissions() {
    try {
      const result = await PushNotifications.requestPermissions();
      if (result.receive === 'granted') {
        await PushNotifications.register();
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Push notification permissions denied:', error);
      return false;
    }
  }

  static async getDeviceToken(): Promise<string | null> {
    try {
      if (this.isNative()) {
        // For native platforms (iOS/Android)
        const permissionGranted = await this.requestPushPermissions();
        if (!permissionGranted) {
          return null;
        }

        return new Promise((resolve) => {
          PushNotifications.addListener('registration', (token) => {
            resolve(token.value);
          });

          PushNotifications.addListener('registrationError', (error) => {
            console.error('Push notification registration error:', error);
            resolve(null);
          });
        });
      } else {
        // For web platform using service worker
        return await this.getWebPushToken();
      }
    } catch (error) {
      console.error('Error getting device token:', error);
      return null;
    }
  }

  private static async getWebPushToken(): Promise<string | null> {
    try {
      console.log('🌐 Starting web push token generation...');
      
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('❌ Push messaging is not supported in this browser');
        return null;
      }

      console.log('✅ Browser supports push messaging');

      // Register service worker if not already registered
      console.log('🔧 Registering service worker...');
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('✅ Service worker registered successfully');
      
      // Wait for service worker to be ready
      console.log('⏳ Waiting for service worker to be ready...');
      const serviceWorker = await navigator.serviceWorker.ready;
      console.log('✅ Service worker is ready');

      // Check if already subscribed
      console.log('🔍 Checking for existing push subscription...');
      let subscription = await serviceWorker.pushManager.getSubscription();
      
      if (subscription) {
        console.log('✅ Found existing push subscription');
      } else {
        console.log('📝 No existing subscription found, creating new one...');
        
        // Request permission
        console.log('🔔 Requesting notification permission...');
        const permission = await Notification.requestPermission();
        console.log('🔔 Notification permission:', permission);
        
        if (permission !== 'granted') {
          console.warn('❌ Notification permission denied');
          return null;
        }

        console.log('✅ Notification permission granted');

        // Get VAPID public key
        console.log('🔑 Getting VAPID public key...');
        const vapidKey = await this.getVapidPublicKey();
        console.log('✅ VAPID key obtained');

        // Create new subscription
        console.log('📱 Creating push subscription...');
        subscription = await serviceWorker.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidKey
        });
        console.log('✅ Push subscription created successfully');
      }

      const subscriptionJson = JSON.stringify(subscription);
      console.log('📦 Web push token generated, length:', subscriptionJson.length);
      return subscriptionJson;
    } catch (error) {
      console.error('❌ Error getting web push token:', error);
      return null;
    }
  }

  private static async getVapidPublicKey(): Promise<string> {
    try {
      console.log('🔑 Fetching VAPID public key from server...');
      
      const response = await fetch('https://miygybeltetsajkjsrwy.supabase.co/functions/v1/get-vapid-key', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1peWd5YmVsdGV0c2Fqa2pzcnd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDgxMjAwNTQsImV4cCI6MjA2MzY5NjA1NH0.FM9A1y5eklgE6vofImQCqyp4NSh4iNiM1cJre3SJ20k`,
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1peWd5YmVsdGV0c2Fqa2pzcnd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDgxMjAwNTQsImV4cCI6MjA2MzY5NjA1NH0.FM9A1y5eklgE6vofImQCqyp4NSh4iNiM1cJre3SJ20k',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch VAPID key: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ VAPID public key fetched successfully');
      return data.publicKey;
    } catch (error) {
      console.error('❌ Error fetching VAPID public key:', error);
      // Fallback to a hardcoded key if the server is unreachable
      console.warn('⚠️ Using fallback VAPID key');
      throw new Error('Failed to get VAPID public key');
    }
  }

  static async takePicture() {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt, // Let user choose camera or gallery
      });
      return image.dataUrl;
    } catch (error) {
      console.error('Error taking picture:', error);
      throw error;
    }
  }

  static async getCurrentPosition() {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
      });
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      };
    } catch (error) {
      console.error('Error getting location:', error);
      throw error;
    }
  }

  static async watchPosition(callback: (position: any) => void) {
    try {
      const watchId = await Geolocation.watchPosition(
        {
          enableHighAccuracy: true,
          timeout: 30000,
        },
        callback
      );
      return watchId;
    } catch (error) {
      console.error('Error watching position:', error);
      throw error;
    }
  }

  static async clearWatch(watchId: string) {
    await Geolocation.clearWatch({ id: watchId });
  }

  static async vibrate(duration = 100) {
    if (this.isNative()) {
      // Note: Haptics plugin would be better but this is basic vibration
      navigator.vibrate?.(duration);
    }
  }

  // App state management
  static onAppStateChange(callback: (isActive: boolean) => void) {
    document.addEventListener('visibilitychange', () => {
      callback(!document.hidden);
    });
  }

  // Network status
  static async getNetworkStatus() {
    return navigator.onLine;
  }

  // Token refresh handling
  static setupTokenRefreshListener(onTokenRefresh: (newToken: string) => void) {
    if (this.isNative()) {
      // For native platforms, we need to periodically check for token changes
      // since Capacitor doesn't have a direct token refresh event
      this.setupNativeTokenRefreshCheck(onTokenRefresh);
    } else {
      // For web platforms, periodically check for subscription changes
      this.setupWebTokenRefreshCheck(onTokenRefresh);
    }
  }

  private static setupNativeTokenRefreshCheck(onTokenRefresh: (newToken: string) => void) {
    // Check for token changes every 30 minutes for native platforms
    setInterval(async () => {
      try {
        const currentToken = await this.getDeviceToken();
        if (currentToken) {
          onTokenRefresh(currentToken);
        }
      } catch (error) {
        console.error('Error checking native token refresh:', error);
      }
    }, 30 * 60 * 1000); // 30 minutes
  }

  private static setupWebTokenRefreshCheck(onTokenRefresh: (newToken: string) => void) {
    // Check for token changes every 30 minutes for web
    setInterval(async () => {
      try {
        const currentToken = await this.getWebPushToken();
        if (currentToken) {
          onTokenRefresh(currentToken);
        }
      } catch (error) {
        console.error('Error checking web token refresh:', error);
      }
    }, 30 * 60 * 1000); // 30 minutes
  }

  static removeTokenRefreshListeners() {
    if (this.isNative()) {
      PushNotifications.removeAllListeners();
    }
  }
}